import React, { useEffect } from 'react';
import { useCurrentPage, useSetCurrentPage } from './stores/useAppStore';
import { useAuth } from './providers/AuthProvider';
import { useData } from './providers/DataProvider';
import { AppProviders } from './providers/AppProviders';
import Dashboard from './components/Dashboard';
import LinkGenerator from './components/LinkGenerator';
import ApiTest from './components/ApiTest';
import Tools from './components/Tools';
import PriceAlertsHistory from './pages/PriceAlertsHistory';
import Watchlist from './pages/Watchlist';
import WatchlistSelf from './pages/WatchlistSelf';
import KlinePlayback from './pages/KlinePlayback';
import PersonalCenter from './pages/PersonalCenter';
import Wallet from './pages/Wallet';
import Deposit from './pages/Deposit';
import Withdraw from './pages/Withdraw';
import MyMonitors from './pages/MyMonitors';
import SignalTriggers from './pages/SignalTriggers';
import CoinMetricsAnalysis from './pages/CoinMetricsAnalysis';
import MyOrders from './pages/MyOrders';
import MyPendingOrders from './pages/MyPendingOrders';
import UserList from './pages/UserList';
import Backtest from './pages/Backtest';
import StrategySquare from './pages/StrategySquare';
import StrategyDetail from './pages/StrategyDetail';
import SystemTasks from './pages/SystemTasks';
import SystemPrompts from './pages/SystemPrompts';
import AIModelManagement from './pages/AIModelManagement';
import AutoTradingRecords from './pages/AutoTradingRecords';
import HistoricalPositions from './pages/HistoricalPositions';
import SignalScanner from './pages/SignalScanner';
import StockMonitors from './pages/StockMonitors';
import StockDashboard from './pages/StockDashboard';
import StockStrategies from './pages/StockStrategies';
import StockQuantPortfolio from './pages/StockQuantPortfolio';
import StockPortfolio from './pages/StockPortfolio';
import ResearchReports from './pages/ResearchReports';
import StockSignals from './pages/StockSignals';
import { isValidPage } from './utils/telegramLinks';
import GradientBackground from './components/ui/GradientBackground';
import ParticleBackground from './components/ui/ParticleBackground';
import Toast from './components/ui/Toast';

// 应用内容组件 - 使用 Provider 提供的状态
const AppContent: React.FC = () => {
  // 从 Zustand store 获取页面状态
  const currentPage = useCurrentPage();
  const setCurrentPage = useSetCurrentPage();

  // 从 Provider 获取认证状态
  const { isAuthenticated, error: authError } = useAuth();

  // 从 Provider 获取数据状态
  const { isDataLoaded, isLoading: dataLoading, error: dataError } = useData();

  // 解析 Telegram WebApp 启动参数
  useEffect(() => {
    console.log('🔧 解析 Telegram WebApp 启动参数...');

    const telegramWebApp = (window as any).Telegram?.WebApp;
    if (!telegramWebApp) {
      console.log('ℹ️ 没有检测到 Telegram WebApp，使用默认页面');
      return;
    }

    let startPage: string | null = null;

    // 方式1: 通过 startParam 获取启动参数
    if (telegramWebApp.startParam) {
      startPage = telegramWebApp.startParam;
      console.log('✅ 从 startParam 获取到启动参数:', startPage);
    }

    // 方式2: 从 initData 中解析参数
    if (!startPage && telegramWebApp.initData) {
      try {
        const initData = new URLSearchParams(telegramWebApp.initData);
        startPage = initData.get('page') || initData.get('start_param');
        if (startPage) {
          console.log('✅ 从 initData 解析到启动参数:', startPage);
        }
      } catch (error) {
        console.error('解析 initData 失败:', error);
      }
    }

    // 方式3: 从 initDataUnsafe 中获取
    if (!startPage && telegramWebApp.initDataUnsafe) {
      const unsafeData = telegramWebApp.initDataUnsafe;
      startPage = unsafeData.start_param || unsafeData.page;
      if (startPage) {
        console.log('✅ 从 initDataUnsafe 获取到启动参数:', startPage);
      }
    }

    // 方式4: 从 URL 查询参数中获取 startapp 参数
    if (!startPage) {
      const urlParams = new URLSearchParams(window.location.search);
      startPage = urlParams.get('startapp');
      if (startPage) {
        console.log('✅ 从 URL 查询参数获取到启动参数:', startPage);
      }
    }

    // 设置页面
    if (startPage && isValidPage(startPage)) {
      console.log('✅ 检测到有效的启动参数 page:', startPage);
      setCurrentPage(startPage);
    } else if (startPage) {
      console.warn('❌ 无效的页面参数:', startPage);
      console.log('支持的页面类型:', [
        'home',
        'dashboard',
        'quant-account',
        'wallet',
        'deposit',
        'withdraw',
        'profit',
        'settings',
        'api-test',
        'link-generator',
        'tools',
      ]);
    } else {
      console.log('ℹ️ 没有检测到启动参数，使用默认页面');
    }
  }, [setCurrentPage]);

  // 设置主题
  useEffect(() => {
    const telegramWebApp = (window as any).Telegram?.WebApp;
    if (!telegramWebApp) return;

    const handleThemeChanged = () => {
      document.documentElement.style.setProperty(
        '--tg-theme-bg-color',
        telegramWebApp.themeParams.bg_color || '#ffffff',
      );
      document.documentElement.style.setProperty(
        '--tg-theme-text-color',
        telegramWebApp.themeParams.text_color || '#000000',
      );
      document.documentElement.style.setProperty(
        '--tg-theme-hint-color',
        telegramWebApp.themeParams.hint_color || '#999999',
      );
      document.documentElement.style.setProperty(
        '--tg-theme-link-color',
        telegramWebApp.themeParams.link_color || '#2481cc',
      );
      document.documentElement.style.setProperty(
        '--tg-theme-button-color',
        telegramWebApp.themeParams.button_color || '#2481cc',
      );
      document.documentElement.style.setProperty(
        '--tg-theme-button-text-color',
        telegramWebApp.themeParams.button_text_color || '#ffffff',
      );
    };

    // 应用初始主题
    handleThemeChanged();

    // 监听主题变化
    telegramWebApp.onEvent('themeChanged', handleThemeChanged);

    return () => {
      telegramWebApp.offEvent('themeChanged', handleThemeChanged);
    };
  }, []);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
      case 'home':
        return <Dashboard />;
      case 'wallet':
        return <Wallet />;
      case 'deposit':
        return <Deposit />;
      case 'withdraw':
        return <Withdraw />;
      case 'profit':
        return <div className="p-4 text-center">收益功能开发中...</div>;
      case 'settings':
        return <div className="p-4 text-center">设置功能开发中...</div>;
      case 'api-test':
        return <ApiTest />;
      case 'link-generator':
        return <LinkGenerator />;
      case 'tools':
        return <Tools />;
      case 'price-alerts-history':
        return <PriceAlertsHistory />;
      case 'watchlist':
        return <Watchlist />;
      case 'watchlist-self':
        return <WatchlistSelf />;
      case 'my-monitors':
        return <MyMonitors />;
      case 'signal-triggers':
        return <SignalTriggers />;
      case 'kline-playback':
        return <KlinePlayback />;
      case 'personal-center':
        return <PersonalCenter />;
      case 'coin-metrics-analysis':
        return <CoinMetricsAnalysis />;
      case 'my-orders':
        return <MyOrders />;
      case 'my-pending-orders':
        return <MyPendingOrders />;
      case 'user-list':
        return <UserList />;
      case 'backtest':
        return <Backtest />;
      case 'strategy-square':
        return <StrategySquare />;
      case 'strategy-detail':
        return <StrategyDetail />;
      case 'system-tasks':
        return <SystemTasks />;
      case 'system-prompts':
        return <SystemPrompts />;
      case 'ai-model-management':
        return <AIModelManagement />;
      case 'auto-trading-records':
        return <AutoTradingRecords />;
      case 'signal-scanner':
        return <SignalScanner />;
      case 'historical-positions':
        return <HistoricalPositions />;
      case 'stock-monitors':
        return <StockMonitors />;
      case 'stock-dashboard':
        return <StockDashboard />;
      case 'stock-strategies':
        return <StockStrategies />;
      case 'stock-quant-portfolio':
        return <StockQuantPortfolio />;
      case 'stock-portfolio':
        return <StockPortfolio />;
      case 'research-reports':
        return <ResearchReports />;
      case 'stock-signals':
        return <StockSignals />;
      default:
        return <Dashboard />;
    }
  };

  // 认证失败显示错误状态
  if (authError) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
          <div className="text-center">
            <div className="text-red-400 text-6xl mb-4 animate-pulse">⚠️</div>
            <h2 className="text-2xl font-bold mb-2 text-white">认证失败</h2>
            <p className="text-white/70 mb-6">{authError}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-400 hover:to-pink-400 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              重新加载
            </button>
          </div>
        </div>
      </GradientBackground>
    );
  }

  // 未认证状态显示空白，等待 Telegram 触发
  if (!isAuthenticated) {
    return null;
  }

  // 认证成功但正在加载数据
  if (dataLoading || !isDataLoaded) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
        </div>
      </GradientBackground>
    );
  }

  // 数据加载失败
  if (dataError) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
          <div className="text-center">
            <div className="text-red-400 text-6xl mb-4 animate-pulse">⚠️</div>
            <h2 className="text-2xl font-bold mb-2 text-white">数据加载失败</h2>
            <p className="text-white/70 mb-6">{dataError}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-400 hover:to-pink-400 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              重新加载
            </button>
          </div>
        </div>
      </GradientBackground>
    );
  }

  // 认证成功且数据加载完成，显示主应用
  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen relative z-10 overflow-x-hidden">
        {renderCurrentPage()}
        <Toast />
      </div>
    </GradientBackground>
  );
};

// 主应用组件 - 包装 Provider
const App: React.FC = () => {
  return (
    <AppProviders>
      <AppContent />
    </AppProviders>
  );
};

export default App;
